import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import CaseStatisticsPage from "./CaseStatisticsPage";
import { Button, Card, Accordion, Container, Row, Col } from "react-bootstrap";
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';

// Helper function to capitalize first and last name
const capitalizeName = (name) => {
  return name.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

const DirectorCaseSummaryPage = () => {
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("No authentication token found. Please log in again.");
      setLoading(false);
      return;
    }

    fetch(`${API_ENDPOINTS.DIRECTOR}/residents-case-summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Authentication failed. Please log in again.');
          } else if (res.status === 403) {
            throw new Error('You do not have permission to access this resource.');
          } else {
            throw new Error(`Failed to fetch resident data: ${res.status}`);
          }
        }
        return res.json();
      })
      .then(data => {
        if (!Array.isArray(data)) {
          throw new Error('Invalid response format from server');
        }
        console.log('Raw resident data from server:', data);
        setResidents(data);
      })
      .catch(err => {
        setError(err.message);
        console.error('Error fetching residents:', err);
        if (err.message.includes('Authentication failed')) {
          navigate('/login');
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleResidentClick = (resident) => {
    setSelectedResident(resident);
  };

  // Group residents by PGY level
  const residentsByPGY = residents.reduce((acc, resident) => {
    const pgyLevel = resident.year || 'Unknown';
    if (!acc[pgyLevel]) {
      acc[pgyLevel] = [];
    }
    acc[pgyLevel].push(resident);
    return acc;
  }, {});

  // Sort PGY levels
  const sortedPGYLevels = Object.keys(residentsByPGY).sort((a, b) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return parseInt(a) - parseInt(b);
  });

  return (
    <div className="min-vh-100">
      <PageTitle title="Case Logs Overview" description="View and manage resident case logs and statistics" />
      <Navbar pageTitle="Case Logs Overview" />
      
      <Container>
        <Row>
          {/* Sidebar - col-2 */}
          <Col lg={2} md={3} sm={12} xs={12} className="mt-4">
              
              {loading ? (
                <div className="d-flex justify-content-center">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <Card className="shadow-sm border-0 custom-sticky-top mb-3" style={{ top: '5rem' }}>
                  <Card.Header className="p-3 py-4 d-flex border-bottom justify-content-between align-items-center">
                    <h6 className="fw-semibold mb-0">Residents</h6>
                  </Card.Header>
                  <Card.Body className="p-0">

                <Accordion defaultActiveKey={sortedPGYLevels[0]} alwaysOpen={false}>
                  {sortedPGYLevels.map(pgyLevel => (

                    <Accordion.Item eventKey={pgyLevel} key={pgyLevel} className="border-0 custom-accordian-border">
                      <Accordion.Header>
 
                          <span className="fw-semibold">PGY-{pgyLevel}</span>

                      </Accordion.Header>
                      <Accordion.Body className="p-0">
                        <div className="list-group border-0 rounded-0">
                          {residentsByPGY[pgyLevel].map(resident => (
                            <button
                              key={resident.id}
                              className={`list-group-item list-group-item-action border-0 py-2 px-3 ${
                                selectedResident?.id === resident.id ? 'list-group-item active' : ''
                              }`}
                              onClick={() => handleResidentClick(resident)}
                              style={{ 
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-medium">{capitalizeName(resident.name)}</span>
                                <span className={`badge py-1 px-2 ${
                                  selectedResident?.id === resident.id ? 'bg-light text-primary' : 'bg-secondary'
                                }`}>
                                  {resident.totalCases}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
                </Card.Body>
                </Card>
              )}
          </Col>

          {/* Main Content - col-10 */}
          <Col lg={10} md={9} sm={12} xs={12} className="mt-4 mb-3">
            <div >
          {error && (
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
            </div>
          )}
          
                            {!selectedResident ? (
                              <Card className="shadow-sm border-0 p-5" style={{ minHeight: '584px', display: 'flex', flexDirection: 'column' }}>
                                <Card.Body>
                <div className="text-center py-5">
                  <div className="text-muted">
                    <img src="/images/undraw_to-do_06xe.svg" alt="Select a Resident" style={{maxHeight: '200px', height: '100%', width: '100%', objectFit: 'contain' }}/>
                    <h4 className="mt-4">Select a Resident</h4>
                    <p>Choose a resident from the sidebar to view their case statistics</p>
                  </div>
                </div>
                </Card.Body>
                </Card>
              ) : (
                <Card className="shadow-sm border-0">
                  <Card.Header className="p-4 border-bottom d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
                        <div>
                          <h4 className="mb-1">{capitalizeName(selectedResident.name)}</h4>
                          <p className="text-muted mb-0">
                            PGY-{selectedResident.year} • {selectedResident.totalCases} Total Cases
                          </p>
                        </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/director/case-logs/${selectedResident.id}`)}
                      >
                        <i className="bi bi-box-arrow-up-right me-2"></i>
                        View case report
                      </Button>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <CaseStatisticsPage embedded residentId={selectedResident.id} />
                  </Card.Body>
                </Card>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DirectorCaseSummaryPage;
